
import { useState } from "react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { CandidateSelector } from "@/components/candidates/CandidateSelector";
import { JobSelector } from "@/components/jobs/JobSelector";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { PageHeader } from "@/components/PageHeader";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileJson, FileText, Save } from "lucide-react";
import { ScoreGauge } from "@/components/ui/score-gauge";
import { useTranslation } from "react-i18next";

interface Candidate {
    id: string;
    name: string;
    skills?: string[];
}

interface Job {
    id: string;
    title: string;
    skills?: string[];
}

interface MatchResult {
    analysis?: {
        overall_compatibility_score?: number;
        matching_analysis?: Record<string, any>;
        key_strengths?: string[];
        key_gaps?: string[];
        final_recommendation?: string;
    };
}

const MatchTest = () => {
    const { t } = useTranslation();
    const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
    const [selectedJob, setSelectedJob] = useState<Job | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [matchResult, setMatchResult] = useState<MatchResult | null>(null);
    const [temperature, setTemperature] = useState([0.5]);
    const [isSaving, setIsSaving] = useState(false);

    const handleMatch = async () => {
        if (!selectedCandidate || !selectedJob) {
            toast({
                title: t("errors.required"),
                description: t("matchTest.selectBoth"),
                variant: "destructive",
            });
            return;
        }

        setIsProcessing(true);

        try {
            const { data, error } = await supabase.functions.invoke('full-match', {
                body: {
                    candidateId: selectedCandidate.id,
                    jobId: selectedJob.id,
                    temperature: temperature[0]
                }
            });

            if (error) throw error;

            //console.log("Match result data:", data);
            setMatchResult({ analysis: data });

            toast({
                title: t("matchTest.matchCompleted"),
                description: t("matchTest.analysisCompleted"),
            });
        } catch (error) {
            console.error("Error processing match:", error);
            toast({
                title: t("errors.error"),
                description: t("matchTest.analysisFailed"),
                variant: "destructive",
            });
        } finally {
            setIsProcessing(false);
        }
    };

    const handleSaveMatch = async () => {
        if (!selectedCandidate || !selectedJob || !matchResult?.analysis) {
            toast({
                title: t("matchTest.cannotSaveMatch"),
                description: t("matchTest.ensureSelection"),
                variant: "destructive",
            });
            return;
        }

        setIsSaving(true);

        try {
            const { data: existingMatch } = await supabase
                .from('matches')
                .select('id')
                .eq('candidate_id', selectedCandidate.id)
                .eq('job_id', selectedJob.id)
                .single();

            const matchData = {
                score: matchResult.analysis.overall_compatibility_score
                    ? Math.round(matchResult.analysis.overall_compatibility_score * 100)
                    : 0,
                strengths: matchResult.analysis.key_strengths || [],
                weaknesses: matchResult.analysis.key_gaps || [],
                ai_analysis: matchResult.analysis
            };

            if (existingMatch) {
                const { error } = await supabase
                    .from('matches')
                    .update(matchData)
                    .eq('id', existingMatch.id);

                if (error) throw error;

                toast({
                    title: t("matchTest.matchUpdated"),
                    description: t("matchTest.updateSuccess"),
                });
            } else {
                const { data, error } = await supabase
                    .from('matches')
                    .insert({
                        ...matchData,
                        candidate_id: selectedCandidate.id,
                        job_id: selectedJob.id,
                    })
                    .select();

                if (error) throw error;

                // also trigger a call to ai-processing
                await supabase.functions.invoke('ai-processing?command=push-new-analysis', {
                    method: 'POST',
                    body: {
                        entityId: data[0].id, // Use the match ID as entityId
                        entityType: "match",
                        analysisType: "full-match",
                        actionOnComplete: "update-match",
                    }
                });

                toast({
                    title: t("matchTest.matchSaved"),
                    description: t("matchTest.saveSuccess"),
                });
            }
        } catch (error) {
            console.error("Error saving match:", error);
            toast({
                title: t("errors.error"),
                description: t("matchTest.saveFailed"),
                variant: "destructive",
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="container py-6 space-y-6">
            <PageHeader
                title={t("matchTest.title")}
                description={t("matchTest.description")}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                <h1 style={{color: "red"}}>THIS PAGE IS NOT WORKING ANYMORE</h1>

                <Card className="p-4">
                    <h2 className="text-lg font-medium mb-4">{t("matchTest.selectCandidate")}</h2>
                    <CandidateSelector
                        onSelectCandidate={(candidate) => setSelectedCandidate(candidate)}
                        buttonClassName="w-full"
                    />
                    {selectedCandidate && (
                        <div className="mt-4 p-2 border rounded-sm">
                            <p className="font-medium">{selectedCandidate.name}</p>
                            {selectedCandidate.skills && (
                                <p className="text-sm text-muted-foreground mt-1">
                                    {t("matchTest.skills")}: {selectedCandidate.skills.slice(0, 5).join(", ")}
                                    {selectedCandidate.skills.length > 5 && "..."}
                                </p>
                            )}
                        </div>
                    )}
                </Card>

                <Card className="p-4">
                    <h2 className="text-lg font-medium mb-4">{t("matchTest.selectJob")}</h2>
                    <JobSelector
                        onSelectJob={(job) => setSelectedJob(job)}
                        buttonClassName="w-full"
                    />
                    {selectedJob && (
                        <div className="mt-4 p-2 border rounded-sm">
                            <p className="font-medium">{selectedJob.title}</p>
                            {selectedJob.skills && (
                                <p className="text-sm text-muted-foreground mt-1">
                                    {t("matchTest.skills")}: {selectedJob.skills.slice(0, 5).join(", ")}
                                    {selectedJob.skills.length > 5 && "..."}
                                </p>
                            )}
                        </div>
                    )}
                </Card>
            </div>

            <div className="flex flex-col items-center gap-6 my-4">
                <div className="w-full max-w-md space-y-2">
                    <label className="text-sm text-muted-foreground">
                        {t("matchTest.temperature")}: {temperature[0].toFixed(2)}
                    </label>
                    <Slider
                        value={temperature}
                        onValueChange={setTemperature}
                        min={0}
                        max={2}
                        step={0.01}
                        className="w-full"
                    />
                </div>
                <div className="flex gap-4">
                    <Button
                        size="lg"
                        onClick={handleMatch}
                        disabled={!selectedCandidate || !selectedJob || isProcessing}
                    >
                        {isProcessing ? t("matchTest.processing") : t("matchTest.runAnalysis")}
                    </Button>
                    {matchResult && (
                        <Button
                            size="lg"
                            onClick={handleSaveMatch}
                            disabled={isSaving || !matchResult?.analysis}
                            variant="outline"
                        >
                            <Save className="w-4 h-4 mr-2" />
                            {isSaving ? t("matchTest.saving") : t("matchTest.saveMatch")}
                        </Button>
                    )}
                </div>
            </div>

            {matchResult?.analysis && matchResult.analysis.overall_compatibility_score !== undefined && (
                <div className="flex justify-center my-8">
                    <ScoreGauge score={matchResult.analysis.overall_compatibility_score * 100} />
                </div>
            )}

            <Tabs defaultValue="raw" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="raw">
                        <FileJson className="mr-2 h-4 w-4" /> {t("matchTest.rawResults")}
                    </TabsTrigger>
                    <TabsTrigger value="formatted">
                        <FileText className="mr-2 h-4 w-4" /> {t("matchTest.formattedResults")}
                    </TabsTrigger>
                </TabsList>
                <TabsContent value="raw">
                    <Card className="p-6">
                        <h2 className="text-xl font-medium mb-4">{t("matchTest.rawResults")}</h2>
                        <pre className="bg-gray-100 p-4 rounded-sm overflow-x-auto">
                            {matchResult ? JSON.stringify(matchResult, null, 2) : t("matchTest.noResults")}
                        </pre>
                    </Card>
                </TabsContent>
                <TabsContent value="formatted">
                    <Card className="p-6">
                        <h2 className="text-xl font-medium mb-4">{t("matchTest.formattedResults")}</h2>
                        {matchResult?.analysis ? (
                            <div className="space-y-4">
                                <p>
                                    <strong>{t("matchTest.overallScore")}:</strong>{" "}
                                    {matchResult.analysis.overall_compatibility_score !== undefined
                                        ? (matchResult.analysis.overall_compatibility_score * 100).toFixed(2) + '%'
                                        : 'N/A'}
                                </p>
                                <p>
                                    <strong>{t("matchTest.keyStrengths")}:</strong>{" "}
                                    {matchResult.analysis.key_strengths && matchResult.analysis.key_strengths.length > 0
                                        ? matchResult.analysis.key_strengths.join(", ")
                                        : t("matchTest.noStrengths")}
                                </p>
                                <p>
                                    <strong>{t("matchTest.keyGaps")}:</strong>{" "}
                                    {matchResult.analysis.key_gaps && matchResult.analysis.key_gaps.length > 0
                                        ? matchResult.analysis.key_gaps.join(", ")
                                        : t("matchTest.noGaps")}
                                </p>
                                <p>
                                    <strong>{t("matchTest.finalRecommendation")}:</strong>{" "}
                                    {matchResult.analysis.final_recommendation || t("matchTest.noRecommendation")}
                                </p>
                            </div>
                        ) : (
                            <div className="flex items-center justify-center h-[200px] text-muted-foreground">
                                {t("matchTest.runMatchPrompt")}
                            </div>
                        )}
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default MatchTest;
